// Shared branding panel + mobile header for the auth pages (Login, Signup,
// ResetPassword, VerifyEmail). One source of truth for the panel copy so the
// pages can't drift apart (see PRD/PRD_LandingPageRedesign.md, auth addendum).
//
// Copy rules (synced with the landing page):
//  - No unverifiable claims: no "accurate", no uptime guarantees.
//  - Checklist items must be features that verifiably exist.
import PropTypes from "prop-types";
import Icon from "../icons/Icon";
import { Link } from "react-router-dom";
import { Wordmark } from "../Wordmark";
import iconDark from "../../assets/brand/sahod-icon-dark.svg";
import iconLight from "../../assets/brand/sahod-icon-light.svg";

const CHECKLIST = [
  "SSS, PhilHealth, Pag-IBIG & BIR deduction tracking",
  "Holiday, OT & night-differential premiums",
  "Maker-checker approvals",
  "Bank-ready CSV exports",
];

const CheckIcon = () => (
  <Icon name="check" />
);

// Logo lockup: renders as a Link to "/" by default, or as a button when the
// page needs custom navigation (VerifyEmail signs the user out first).
const LogoLockup = ({ className, icon, onLogoClick }) => {
  const content = (
    <>
      <img className="auth-branding__logo-icon" src={icon} alt="" />
      <Wordmark />
    </>
  );
  if (onLogoClick) {
    return (
      <button
        type="button"
        onClick={onLogoClick}
        className={className}
        style={{ background: "none", border: "none", cursor: "pointer" }}
      >
        {content}
      </button>
    );
  }
  return (
    <Link to="/" className={className}>
      {content}
    </Link>
  );
};

/**
 * Left gradient branding panel (desktop only).
 *
 * Default content is the synced headline/tagline/checklist/badges. Pass
 * `headline`/`tagline` to override the text, or `children` to replace the
 * checklist+badges block entirely (VerifyEmail renders its step list).
 */
export const AuthBrandingPanel = ({
  sticky = false,
  onLogoClick,
  headline,
  tagline,
  children,
}) => (
  <div className={`auth-branding${sticky ? " auth-branding--sticky" : ""}`}>
    <div className="auth-branding__decoration auth-branding__decoration--1"></div>
    <div className="auth-branding__decoration auth-branding__decoration--2"></div>
    <div className="auth-branding__decoration auth-branding__decoration--3"></div>

    <div className="auth-branding__content">
      <LogoLockup
        className="auth-branding__logo"
        icon={iconDark}
        onLogoClick={onLogoClick}
      />
      <h1 className="auth-branding__headline">
        {headline ?? (
          <>
            Philippine payroll
            <br />
            made simple
          </>
        )}
      </h1>
      <p className="auth-branding__tagline">
        {tagline ??
          "Time in, payroll out — one platform built for Philippine SMEs."}
      </p>

      {children ?? (
        <>
          <ul className="auth-branding__features">
            {CHECKLIST.map((item) => (
              <li className="auth-branding__feature" key={item}>
                <span className="auth-branding__check">
                  <CheckIcon />
                </span>
                {item}
              </li>
            ))}
          </ul>

          <div className="auth-branding__badges">
            <div className="auth-branding__badge">
              <span className="auth-branding__badge-icon"><Icon name="lock" size={16} /></span>
              Data Privacy
            </div>
            <div className="auth-branding__badge">
              <span className="auth-branding__badge-icon"><Icon name="building-2" size={16} /></span>
              Isolated company data
            </div>
          </div>
        </>
      )}
    </div>
  </div>
);

/** Centered brand header shown only on mobile, at the top of the form panel. */
export const AuthMobileHeader = ({ onLogoClick }) => (
  <div className="auth-mobile-header">
    <LogoLockup
      className="auth-mobile-logo"
      icon={iconLight}
      onLogoClick={onLogoClick}
    />
  </div>
);

LogoLockup.propTypes = {
  className: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  onLogoClick: PropTypes.func,
};

AuthBrandingPanel.propTypes = {
  sticky: PropTypes.bool,
  onLogoClick: PropTypes.func,
  headline: PropTypes.node,
  tagline: PropTypes.node,
  children: PropTypes.node,
};

AuthMobileHeader.propTypes = {
  onLogoClick: PropTypes.func,
};
